'use client';

import React, { useState } from 'react';
import { Container, SectionLabel, SectionRule } from '../ReferenceChrome';
import { trackFAQOpen } from '@/lib/analytics';

const FAQS = [
  {
    q: 'What is RankGrip for?',
    a: 'RankGrip interprets your Google Search Console data into what is happening on your site, what matters most right now, and what to do next. You should not have to be your own SEO analyst.',
  },
  {
    q: 'Do I need Google Search Console?',
    a: 'Yes for the core product. Live GSC is how RankGrip stays right about your site instead of giving generic SEO advice. Connect it during onboarding.',
  },
  {
    q: 'Is this an AI content writer?',
    a: 'No. Content is an execution step when a GSC-backed plan says a specific gap needs a specific page. The product identity is the diagnostic and the action plan.',
  },
  {
    q: 'What do I see on day one?',
    a: 'A plain-English summary: your Search Console performance, sitemap status, and your first list of pre-written tasks—like optimized title suggestions, page refresh plans, and advice on merging competing URLs.',
  },
  {
    q: 'How is this different from Ahrefs or ChatGPT?',
    a: 'Those tools hand you numbers or drafts and trust you to figure out the next move. RankGrip does the interpreting and keeps a plan so every session has a clear next step.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. There are no long-term contracts. Cancel from account settings; your plan stays active until the end of the billing period.',
  },
];

export function FAQRef() {
  const [open, setOpen] = useState<number | null>(null);

  const toggle = (i: number) => {
    const item = FAQS[i];
    if (open !== i) trackFAQOpen(item.q);
    setOpen(open === i ? null : i);
  };

  return (
    <section className="pb-16 sm:pb-24">
      <SectionRule />
      <Container className="pt-16 sm:pt-24">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1fr_2fr] md:gap-16">
          {/* Left sticky header */}
          <div>
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="mt-3 text-3xl font-normal tracking-tight text-gray-900 sm:text-4xl">
              Frequently asked questions
            </h2>
            <p className="mt-4 text-sm font-light leading-relaxed text-gray-500">
              Everything you need to know before getting started.{' '}
              <a
                href="mailto:teamrankgrip@gmail.com"
                className="font-medium text-[#129990] underline-offset-4 hover:underline"
              >
                Still have questions?
              </a>
            </p>
          </div>

          {/* Accordion */}
          <div className="divide-y divide-gray-100">
            {FAQS.map((item, i) => (
              <div key={i} className="py-4">
                <button
                  className="flex w-full items-start justify-between gap-4 text-left"
                  onClick={() => toggle(i)}
                  aria-expanded={open === i}
                >
                  <span className="text-sm font-medium text-gray-900">{item.q}</span>
                  <span
                    className={`mt-0.5 shrink-0 text-[#129990] transition-transform duration-200 ${
                      open === i ? 'rotate-45' : ''
                    }`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {open === i && (
                  <p className="mt-3 text-sm font-light leading-relaxed text-gray-500">
                    {item.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
